// src/utils/AnalyticsLoader.js
import { useEffect, useRef } from "react";
import TagManager from "react-gtm-module";
import ReactPixel from "react-facebook-pixel";
import ReactGA from "react-ga";

// Helper: insert a <script> into <head> if not already there
function injectScript(src, id) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

export default function AnalyticsLoader({
  gtmId,
  fbPixelId,
  gaTrackingId,
  googleAdsId,
}) {
  const didInitGTM = useRef(false);
  const didInitPixel = useRef(false);
  const didInitGA = useRef(false);
  const didInitAds = useRef(false);

  useEffect(() => {
    // --- 1) Google Tag Manager ---
    if (gtmId && !didInitGTM.current) {
      TagManager.initialize({ gtmId });
      didInitGTM.current = true;
      console.log("[AnalyticsLoader] GTM initialized:", gtmId);
    }

    // --- 2) Facebook Pixel ---
    if (fbPixelId && !didInitPixel.current) {
      ReactPixel.init(fbPixelId);
      ReactPixel.pageView();
      didInitPixel.current = true;
      console.log("[AnalyticsLoader] Facebook Pixel initialized:", fbPixelId);
    }

    // --- 3) Google Analytics (standalone) ---
    if (gaTrackingId && !didInitGA.current) {
      injectScript(
        `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`,
        "ga-tag-script"
      );
      ReactGA.initialize(gaTrackingId);
      ReactGA.pageview(window.location.pathname + window.location.search);
      didInitGA.current = true;
      console.log("[AnalyticsLoader] GA initialized:", gaTrackingId);
    }

    // --- 4) Google Ads (“gtag.js” for Ads) ---
    // If you want Ads tags outside of GTM, you need to load gtag.js with your AW-ID:
    if (googleAdsId && !didInitAds.current) {
      // 4a) Inject the gtag.js library for Ads
      injectScript(
        `https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`,
        "ads-gtag-script"
      );

      // 4b) Initialise gtag for Ads (and optional GA if you want to combine)
      const inlineCode = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        // Configure your Ads ID
        gtag('config', '${googleAdsId}');
      `;
      if (!document.getElementById("ads-gtag-inline")) {
        const s = document.createElement("script");
        s.id = "ads-gtag-inline";
        s.innerHTML = inlineCode;
        document.head.appendChild(s);
      }

      didInitAds.current = true;
      console.log("[AnalyticsLoader] Google Ads initialized:", googleAdsId);
    }
  }, [gtmId, fbPixelId, gaTrackingId, googleAdsId]);

  return null;
}
